import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter from '@/services/utils/figureFormatter';
import pdfHeaderFooter from './pdfHeaderFooter';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const sulaimanLipiFont = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}
pdfMake.fonts = sulaimanLipiFont;

const orderProductionSummaryPdf = () => {
    const { commaFormat } = figureFormatter();
    const { getHeader, getFooter } = pdfHeaderFooter();

    const exportToPDF = (company, summary, lineItems, totals, userName, qrcode, activity = []) => {
        pdfMake.fonts = sulaimanLipiFont;

        const docDefinition = {
            pageSize: 'A4',
            pageMargins: [30, 100, 30, 80],
            header: (currentPage) => {
                if (currentPage === 1) return getHeader(company);
                return { text: company.name, alignment: 'center', fontSize: 12, margin: [0, 20, 0, 0] };
            },
            footer: (currentPage) => {
                if (currentPage === 1) return getFooter(userName, qrcode);
                return null;
            },
            content: [
                { text: 'Order Summary', fontSize: 14, bold: true, alignment: 'center', margin: [0, 0, 0, 15] },
                getCustomerInfoSection(summary),
                getStatsSection(summary, totals),
                getLineItemsTable(lineItems, totals),
                getActivitySection(activity)
            ],
            defaultStyle: {
                fontSize: 9,
                color: '#333',
                font: 'SulaimanLipi'
            }
        };

        pdfMake.createPdf(docDefinition).open();
    };

    const getInfoLine = (label, value) => {
        if (!value) return [];
        return [{ text: `${label}: ${value}` }];
    };

    const getCustomerInfoSection = (summary) => {
        const address = summary.contact_profile_address || summary.contact_address;
        return {
            columns: [
                {
                    width: '50%',
                    stack: [
                        { text: 'Customer Information', bold: true, margin: [0, 0, 0, 4] },
                        ...getInfoLine('Customer Name', summary.contact_name),
                        ...getInfoLine('Contact Person', summary.contact_person),
                        ...getInfoLine('Address', address)
                    ]
                },
                {
                    width: '50%',
                    stack: [
                        { text: ' ', margin: [0, 0, 0, 4] },
                        ...getInfoLine('Mobile', summary.contact_phone_numbers),
                        ...getInfoLine('Bill No', summary.bill_number),
                        ...getInfoLine('Status', summary.status)
                    ]
                }
            ],
            fontSize: 9,
            margin: [0, 0, 0, 12]
        };
    };

    const getStatCard = (label, value) => {
        return {
            table: {
                widths: ['100%'],
                heights: [20, 16],
                body: [
                    [{ text: label, bold: true, fontSize: 8, color: 'black', alignment: 'center', fillColor: '#f2f2f2', margin: [4, 3, 4, 0] }],
                    [{ text: String(value), bold: true, fontSize: 11, color: 'black', alignment: 'center', fillColor: '#f2f2f2', margin: [4, 0, 4, 3] }]
                ]
            },
            layout: {
                hLineWidth: function (i, node) {
                    return (i === 0 || i === node.table.body.length) ? 0.5 : 0;
                },
                vLineWidth: function (i, node) {
                    return (i === 0 || i === node.table.widths.length) ? 0.5 : 0;
                },
                hLineColor: function () { return 'black'; },
                vLineColor: function () { return 'black'; }
            }
        };
    };

    const getStatsSection = (summary, totals) => {
        return {
            stack: [
                {
                    columns: [
                        getStatCard('Order Qty', totals.total_pi_quantity || 0),
                        getStatCard('Production Qty', totals.total_production_quantity || 0),
                        getStatCard('Delivered Qty', totals.total_delivered_quantity || 0),
                        getStatCard('Pending Delivery Qty', totals.pending_delivery_quantity || 0),
                        getStatCard('Ready for Delivery Qty', totals.ready_for_delivery_quantity || 0)
                    ],
                    columnGap: 8,
                    margin: [0, 0, 0, 8]
                },
                {
                    columns: [
                        getStatCard('Pending Production Qty', totals.pending_production_quantity || 0),
                        getStatCard('Order Value', commaFormat(summary.total_amount)),
                        getStatCard('Delivery Value', commaFormat(totals.total_delivery_amount)),
                        getStatCard('Paid Amount', commaFormat(summary.paid_amount)),
                        getStatCard('Due Amount', commaFormat(totals.due_amount))
                    ],
                    columnGap: 8
                }
            ],
            margin: [0, 0, 0, 12]
        };
    };

    const getLineItemsTable = (lineItems, totals) => {
        return {
            table: {
                headerRows: 1,
                widths: ['*', 40, 55, 45, 55, 60],
                body: [
                    [
                        { text: 'Item Details', bold: true },
                        { text: 'PI Qty', bold: true, alignment: 'right' },
                        { text: 'Production Qty', bold: true, alignment: 'center' },
                        { text: 'Delivery Qty', bold: true, alignment: 'center' },
                        { text: 'Unit Price', bold: true, alignment: 'right' },
                        { text: 'Total Amount', bold: true, alignment: 'right' }
                    ],
                    ...lineItems.map((item) => [
                        { text: item.product_name || '' },
                        { text: item.quantity || 0, alignment: 'right' },
                        { text: item.total_pq || 0, alignment: 'center' },
                        { text: item.total_del_qty || 0, alignment: 'center' },
                        { text: commaFormat(item.rate), alignment: 'right' },
                        { text: commaFormat(item.amount), alignment: 'right' }
                    ]),
                    [
                        { text: 'Subtotal', bold: true, colSpan: 5, alignment: 'right' },
                        {}, {}, {}, {},
                        { text: commaFormat(totals.total_line_amount), bold: true, alignment: 'right' }
                    ]
                ]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                fillColor: (rowIndex) => (rowIndex === 0) ? '#f3f2f7' : null
            }
        };
    };

    const getActivitySection = (activity) => {
        if (!activity.length) return {};

        return {
            table: {
                headerRows: 1,
                widths: ['*', 75, '*', 70],
                body: [
                    [
                        { text: 'Activity', bold: true },
                        { text: 'Date', bold: true },
                        { text: 'By', bold: true },
                        { text: 'Bill No', bold: true }
                    ],
                    ...activity.map((event) => [
                        { text: event.title || '' },
                        { text: event.date || '' },
                        { text: event.user_name || '' },
                        { text: event.bill_number || '' }
                    ])
                ]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                fillColor: (rowIndex) => (rowIndex === 0) ? '#f3f2f7' : null
            },
            margin: [0, 12, 0, 0]
        };
    };

    return {
        exportToPDF
    };
};

export default orderProductionSummaryPdf;
